import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowRight,
  Building2,
  CalendarClock,
  Car,
  CheckCircle2,
  MapPin,
  MessageCircleMore,
  Sparkles,
  Wrench,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";

type Row = Record<string, any>;
const content: Record<
  string,
  { title: string; copy: string; features: string[] }
> = {
  "real-estate": {
    title: "Find property with clarity",
    copy: "Buy, rent or sell across the UAE with transparent advice and a complete transaction team.",
    features: ["Curated listings", "Area intelligence", "End-to-end support"],
  },
  cars: {
    title: "Drive something exceptional",
    copy: "Luxury, sports, SUV and chauffeur bookings with clear availability and pricing.",
    features: ["Live fleet", "Flexible rental", "Doorstep delivery"],
  },
  cleaning: {
    title: "A cleaner space, on schedule",
    copy: "Book trusted residential and commercial cleaning as a visit or recurring plan.",
    features: ["Verified teams", "Flexible packages", "Quality checks"],
  },
  maintenance: {
    title: "Repairs managed properly",
    copy: "From urgent callouts to preventive maintenance, every job stays accountable.",
    features: ["Fast triage", "Approved quotes", "Service warranty"],
  },
  "business-center": {
    title: "An office ready for business",
    copy: "Available offices, controlled onboarding and building services in one place.",
    features: ["Flexible offices", "Managed internet", "Complete handover"],
  },
  "holiday-homes": {
    title: "Stay with confidence",
    copy: "Professionally managed UAE holiday homes with a smooth guest experience.",
    features: ["Verified homes", "Easy check-in", "Guest support"],
  },
};

export function PublicBusinessSite({ kind }: { kind: string }) {
  const page = content[kind];
  const [items, setItems] = useState<Row[]>([]),
    [selected, setSelected] = useState<Row | null>(null),
    [message, setMessage] = useState("");
  useEffect(() => {
    const availableKind = kind === "business-center" ? "real-estate" : kind;
    fetch(`/api/v1/public/catalog/${availableKind}`)
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((x) => setItems(x.data || []))
      .catch(() => setItems([]));
  }, [kind]);
  const isService = ["cleaning", "maintenance"].includes(kind);
  return (
    <div className={`public-page public-${kind} public-business-site`}>
      <header>
        <img
          className="public-wordmark"
          src="/realco-wordmark.png"
          alt="Realco"
        />
        <nav>
          <a href="#availability">Availability</a>
          <Link to="/customer">My account</Link>
          <Link to="/login" className="button primary small">
            Sign in
          </Link>
        </nav>
      </header>
      <main>
        <section className="public-hero">
          <div>
            <span className="kicker light">Realco group · UAE</span>
            <h1>{page.title}</h1>
            <p>{page.copy}</p>
            <div className="public-features">
              {page.features.map((x) => (
                <span key={x}>
                  <CheckCircle2 />
                  {x}
                </span>
              ))}
            </div>
            <div className="public-actions">
              <a href="#availability" className="button white">
                Explore availability <ArrowRight />
              </a>
              <a href="mailto:hello@realcocapital.ae" className="button glass">
                <MessageCircleMore />
                Speak to us
              </a>
            </div>
          </div>
          <Sparkles className="public-orbit" />
        </section>
        <section id="availability" className="public-catalog">
          <header>
            <span className="kicker">Live catalogue</span>
            <h2>{isService ? "Book a service" : "Available now"}</h2>
            <p>Only approved and active items are shown.</p>
          </header>
          <div>
            {items.map((item) => (
              <article key={item.id}>
                <div className="public-card-icon">
                  {kind === "cars" ? (
                    <Car />
                  ) : isService ? (
                    <Wrench />
                  ) : (
                    <Building2 />
                  )}
                </div>
                <span>
                  {item.community || item.trade_name || item.make || "Realco"}
                </span>
                <h3>
                  {item.title_en ||
                    item.name_en ||
                    `${item.make || ""} ${item.model || ""}`}
                </h3>
                <p>
                  {item.description_en ||
                    [
                      item.property_type,
                      item.bedrooms && `${item.bedrooms} bedrooms`,
                      item.built_up_area_sqft &&
                        `${item.built_up_area_sqft} sq ft`,
                    ]
                      .filter(Boolean)
                      .join(" · ") ||
                    "Contact our team for full details."}
                </p>
                <footer>
                  <strong>
                    {Number(item.base_price || item.daily_rate || 0) > 0
                      ? new Intl.NumberFormat("en-AE", {
                          style: "currency",
                          currency: item.currency || "AED",
                          maximumFractionDigits: 0,
                        }).format(item.base_price || item.daily_rate)
                      : "Enquire"}
                  </strong>
                  {isService || kind === "cars" ? (
                    <button onClick={() => setSelected(item)}>
                      {kind === "cars" ? "Request rental" : "Book"} <ArrowRight />
                    </button>
                  ) : (
                    <a href="mailto:hello@realcocapital.ae">
                      Enquire <ArrowRight />
                    </a>
                  )}
                </footer>
              </article>
            ))}
            {!items.length && (
              <div className="public-empty">
                <CalendarClock />
                <h3>Availability is being prepared</h3>
                <p>
                  Contact the team and we will respond with current options.
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <footer>
        Powered by <strong>Workwise</strong> · Copyright © 2026
      </footer>
      {selected && (
        <div className="modal-backdrop">
          <form
            className="modal public-booking-modal"
            onSubmit={async (event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              const d = Object.fromEntries(new FormData(event.currentTarget));
              const rental = kind === "cars";
              const response = await fetch(rental ? "/api/v1/public/rental-requests" : "/api/v1/public/service-bookings", {
                method: "POST",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(rental ? {
                  vehicleId: selected.id,
                  customerName: d.customerName,
                  email: d.email || undefined,
                  mobile: d.mobile || undefined,
                  pickupAt: new Date(String(d.pickupAt)).toISOString(),
                  returnAt: new Date(String(d.returnAt)).toISOString(),
                  pickupLocation: d.pickupLocation,
                  returnLocation: d.returnLocation || d.pickupLocation,
                  deliveryRequired: d.deliveryRequired === "on",
                  chauffeurRequired: d.chauffeurRequired === "on",
                  driverAge: d.driverAge ? Number(d.driverAge) : undefined,
                  licenceCountry: d.licenceCountry || undefined,
                  passengers: d.passengers ? Number(d.passengers) : undefined,
                  flightNumber: d.flightNumber || undefined,
                  notes: d.notes || undefined,
                } : {
                  serviceId: selected.id,
                  customerName: d.customerName,
                  email: d.email || undefined,
                  mobile: d.mobile || undefined,
                  serviceAddress: d.serviceAddress,
                  emirate: "Dubai",
                  city: "Dubai",
                  area: d.area || undefined,
                  requestedAt: new Date(String(d.requestedAt)).toISOString(),
                  quantity: Number(d.quantity || 1),
                  notes: d.notes || undefined,
                }),
              });
              const result = await response.json();
              if (!response.ok) {
                setMessage(result.message || result.code);
                return;
              }
              setMessage(
                `${rental ? "Rental request" : "Booking"} ${result.data.reference} received. Our team will contact you.`,
              );
              setSelected(null);
            }}
          >
            <header>
              <div>
                <span className="kicker">{kind === "cars" ? "Live fleet rental request" : "Secure booking request"}</span>
                <h2>{selected.name_en || `${selected.make || ""} ${selected.model || ""}`}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setSelected(null)}
              >
                <X />
              </button>
            </header>
            {message && <div className="form-error">{message}</div>}
            <div className="form-grid">
              <label>
                Name
                <input name="customerName" required />
              </label>
              <label>
                Mobile
                <input name="mobile" required placeholder="+971" />
              </label>
              <label>
                Email
                <input name="email" type="email" />
              </label>
              {kind === "cars" ? <>
                <label>Pickup date & time<input name="pickupAt" type="datetime-local" required /></label>
                <label>Return date & time<input name="returnAt" type="datetime-local" required /></label>
                <label>Pickup location<input name="pickupLocation" required placeholder="Dubai Airport / hotel / address" /></label>
                <label>Return location<input name="returnLocation" placeholder="Same as pickup" /></label>
                <label>Driver age<input name="driverAge" type="number" min="18" max="100" /></label>
                <label>Driving licence country<select name="licenceCountry" defaultValue="AE"><option value="AE">United Arab Emirates</option><option value="GB">United Kingdom</option><option value="US">United States</option><option value="IN">India</option><option value="PK">Pakistan</option><option value="SA">Saudi Arabia</option><option value="ZZ">Other</option></select></label>
                <label>Passengers<input name="passengers" type="number" min="1" max="50" /></label>
                <label>Flight number<input name="flightNumber" /></label>
                <label className="check"><input name="deliveryRequired" type="checkbox" />Doorstep delivery</label>
                <label className="check"><input name="chauffeurRequired" type="checkbox" />Chauffeur required</label>
              </> : <>
                <label>Preferred time<input name="requestedAt" type="datetime-local" required /></label>
                <label>Quantity / visits<input name="quantity" type="number" min="1" defaultValue="1" /></label>
                <label>Area<input name="area" placeholder="Dubai Marina" /></label>
                <label className="span-2">Service address<textarea name="serviceAddress" required rows={2} /></label>
              </>}
              <label className="span-2">
                Notes
                <textarea name="notes" rows={3} />
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="button secondary"
                onClick={() => setSelected(null)}
              >
                Cancel
              </button>
              <button className="button primary">
                <MapPin />
                {kind === "cars" ? "Submit rental request" : "Request booking"}
              </button>
            </footer>
          </form>
        </div>
      )}
      {message && !selected && (
        <div className="public-success">
          <CheckCircle2 />
          {message}
        </div>
      )}
    </div>
  );
}
